import { computed } from "vue";
import { useStoreItem } from "../itemStore.store";
import { conditionItemStructure, useConditionStore } from "./conditions.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useActionStore } from "../action/actions.store";
import {
  ensureConditionValidity,
  getConditionTimeForm,
  getValidComparedStartTypes,
  getValidOperators,
  getValidActivatorStartTypes,
  supportsTime
} from "./conditionFunctions";
import type { ConditionItem, ConditionOperator } from "./ConditionItem";

export const useCondition = useStoreItem(
  conditionItemStructure,
  useConditionStore,
  (condition, store) => {
    const targetStore = useTargetStore();
    const resourceStore = useResourceStore();
    const indexStore = useIndexStore();
    const incidentStore = useIncidentStore();
    const actionStore = useActionStore();

    const validOperators = computed((): ConditionOperator[] => {
      if (condition.value == null) return [];

      return getValidOperators(condition.value);
    });

    const operatorValid = computed(() => {
      if (condition.value == null) return false;
      if (condition.value.operator == null) return false;

      return validOperators.value.includes(condition.value.operator);
    });

    const activatorTimeForm = computed(() => {
      if (condition.value == null) return "point";
      return getConditionTimeForm(
        condition.value.activatorType,
        condition.value.activatorProperty
      );
    });

    const comparedTimeForm = computed(() => {
      if (condition.value == null) return "point";
      return getConditionTimeForm(
        condition.value.comparedType,
        condition.value.comparedProperty
      );
    });

    return {
      activatorItem: computed(() => {
        if (condition.value == null) return null;

        if (condition.value.activatorType == null) {
          return null;
        }
        if (condition.value.activatorId == null) {
          return null;
        }
        switch (condition.value.activatorType) {
          case "target":
            return targetStore.items[condition.value.activatorId];
          case "resource":
            return resourceStore.items[condition.value.activatorId];
          case "index":
            return indexStore.items[condition.value.activatorId];
          case "incident":
            return incidentStore.items[condition.value.activatorId];
          case "action":
            return actionStore.items[condition.value.activatorId];
          default:
            return null;
        }
      }),

      comparedItem: computed(() => {
        if (condition.value == null) return null;

        if (condition.value.comparedType == null) {
          return null;
        }
        if (condition.value.comparedId == null) {
          return null;
        }
        switch (condition.value.comparedType) {
          case "target":
            return targetStore.items[condition.value.comparedId];
          case "resource":
            return resourceStore.items[condition.value.comparedId];
          case "index":
            return indexStore.items[condition.value.comparedId];
          case "incident":
            return incidentStore.items[condition.value.comparedId];
          case "action":
            return actionStore.items[condition.value.comparedId];
          default:
            return null;
        }
      }),

      activatorComplete: computed(() => {
        if (
          condition.value?.activatorType == "round" ||
          condition.value?.activatorType == "roundRange"
        ) {
          return true;
        }
        return (
          condition.value?.activatorType != null &&
          condition.value?.activatorId != null &&
          condition.value?.activatorProperty != null
        );
      }),

      supportsActivatorTime: computed(() => {
        if (condition.value == null) return false;
        return supportsTime(
          condition.value.activatorType,
          condition.value.activatorProperty
        );
      }),

      validOperators,
      operatorValid,

      complete: computed(() => {
        if (condition == null) return false;
        if (operatorValid.value == false) return false;

        return true;
      }),

      activatorTimeForm,
      validActivatorStartTypes: computed(
        (): ConditionItem["activatorStartType"][] => {
          if (condition.value == null) return [];
          return getValidActivatorStartTypes(condition.value);
        }
      ),

      validUpdate(changes: Partial<ConditionItem>) {
        if (condition.value == null) throw new Error("Condition is null");

        ensureConditionValidity(condition.value, changes);

        store.update({
          ...changes,
          id: condition.value.id
        });
      },
      validUpdateKey<K extends keyof ConditionItem>(
        key: K,
        value: ConditionItem[K]
      ) {
        if (condition.value == null) throw new Error("Condition is null");

        const changes: Partial<ConditionItem> = {
          [key]: value
        };

        ensureConditionValidity(condition.value, changes);

        store.update({
          ...changes,
          id: condition.value.id
        });
      },

      comparedTimeForm,
      validComparedStartTypes: computed(
        (): ConditionItem["comparedStartType"][] => {
          if (condition.value == null) return [];
          return getValidComparedStartTypes(condition.value);
        }
      ),
      supportsComparedTime: computed(() => {
        if (condition.value == null) return false;
        return supportsTime(condition.value.comparedType, condition.value.comparedProperty);
      })
    };
  }
);
